import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Check, X, Search, Edit, Eye, FileText, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const MaterialIndents = () => {
  const [indents, setIndents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteIndent, setQuoteIndent] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);

  const emptyForm = { department: '', priority: 'normal', notes: '', items: [{ sku: '', name: '', quantity: 0, unit: '', required_by: '', notes: '' }] };
  const [formData, setFormData] = useState({ ...emptyForm });
  const [quoteForm, setQuoteForm] = useState({ vendor_id: '', vendor_name: '', items: [], total_amount: 0, delivery_days: 0, payment_terms: '', remarks: '' });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [iRes, dRes, invRes, vRes] = await Promise.all([
        api.get('/purchase/indents'), api.get('/purchase/departments'),
        api.get('/sales/inventory'), api.get('/purchase/vendors')
      ]);
      setIndents(iRes.data); setDepartments(dRes.data); setInventory(invRes.data); setVendors(vRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setShowModal(true); };
  const openEdit = (indent) => {
    setEditing(indent);
    setFormData({ department: indent.department, priority: indent.priority || 'normal', notes: indent.notes || '', items: indent.items || [{ sku: '', name: '', quantity: 0, unit: '' }] });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/purchase/indents/${editing.id}`, formData);
      } else {
        const res = await api.post('/purchase/indents', formData);
        try { await api.post('/workflow/approvals/request', { document_type: 'indent', document_id: res.data.id, document_number: res.data.indent_number, module: 'purchase', max_level: 3 }); } catch {}
      }
      setShowModal(false); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, { sku: '', name: '', quantity: 0, unit: '', required_by: '', notes: '' }] }));
  const removeItem = (idx) => { if (formData.items.length <= 1) return; setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) })); };
  const updateItem = (idx, field, val) => {
    const items = [...formData.items]; items[idx] = { ...items[idx], [field]: val };
    if (field === 'sku' && val) { const inv = inventory.find(i => i.sku === val); if (inv) { items[idx].name = inv.name; items[idx].unit = inv.unit; } }
    setFormData(prev => ({ ...prev, items }));
  };

  const approveIndent = async (id) => { try { await api.post(`/purchase/indents/${id}/approve`); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); } };
  const rejectIndent = async (id) => { const r = prompt('Rejection reason:'); if (!r) return; try { await api.post(`/purchase/indents/${id}/reject`, { rejected_reason: r }); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); } };

  const viewDetail = async (indent) => {
    try { const r = await api.get(`/purchase/indents/${indent.id}/detail`); setDetailData(r.data); setShowDetailModal(true); }
    catch (e) { alert('Failed to load details'); }
  };

  const openAddQuote = (indent) => {
    setQuoteIndent(indent);
    setQuoteForm({
      indent_id: indent.id, indent_number: indent.indent_number,
      vendor_id: '', vendor_name: '',
      items: (indent.items || []).map(i => ({ sku: i.sku, name: i.name, quantity: i.quantity, unit_price: 0, total: 0 })),
      total_amount: 0, delivery_days: 0, payment_terms: '', remarks: ''
    });
    setShowQuoteModal(true);
  };
  const updateQuoteItem = (idx, field, val) => {
    const items = [...quoteForm.items]; items[idx] = { ...items[idx], [field]: val };
    if (field === 'unit_price') items[idx].total = (parseFloat(val) || 0) * items[idx].quantity;
    const total = items.reduce((s, i) => s + (i.total || 0), 0);
    setQuoteForm(prev => ({ ...prev, items, total_amount: total }));
  };
  const submitQuote = async (e) => {
    e.preventDefault();
    try { await api.post('/purchase/quotations', quoteForm); setShowQuoteModal(false); fetchData(); alert('Quotation added!'); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const openCompare = async (indent) => {
    try { const r = await api.get(`/purchase/quotations/compare/${indent.id}`); setCompareData(r.data); setShowCompareModal(true); }
    catch (e) { alert('Failed to load comparison'); }
  };
  const selectQuote = async (qid) => {
    try { await api.post(`/purchase/quotations/${qid}/select`); alert('Quote selected! You can now create a PO from this vendor.'); setShowCompareModal(false); }
    catch (e) { alert('Failed'); }
  };

  const getStatusBadge = (s) => { const m = { pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-danger', converted: 'badge-info' }; return `badge ${m[s] || 'badge-info'}`; };

  const filtered = indents.filter(indent => {
    const s = searchTerm.toLowerCase();
    const match = !s || (indent.indent_number || '').toLowerCase().includes(s) || (indent.department || '').toLowerCase().includes(s) || (indent.notes || '').toLowerCase().includes(s);
    return match && (statusFilter === 'all' || indent.status === statusFilter);
  });

  return (
    <div data-testid="indents-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Material Indents</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-indent-btn"><Plus size={16} /> Create Indent</button>
      </div>

      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
            <input type="text" className="form-input" placeholder="Search indent #, dept..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
          </div>
          <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
            <option value="all">All Status</option>
            <option value="pending">Pending</option><option value="approved">Approved</option>
            <option value="rejected">Rejected</option><option value="converted">Converted</option>
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Indent #</th><th>Department</th><th>Items</th><th>Priority</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No indents</td></tr> : filtered.map(indent => (
                  <React.Fragment key={indent.id}>
                    <tr data-testid={`indent-row-${indent.indent_number}`} style={{ cursor: 'pointer' }} onClick={() => setExpandedRow(expandedRow === indent.id ? null : indent.id)}>
                      <td className="font-mono">{indent.indent_number}</td>
                      <td>{indent.department}</td>
                      <td>{indent.items?.length || 0} items</td>
                      <td><span className={`badge ${indent.priority === 'high' ? 'badge-danger' : 'badge-info'}`}>{indent.priority || 'normal'}</span></td>
                      <td><span className={getStatusBadge(indent.status)}>{indent.status}</span></td>
                      <td className="text-sm">{new Date(indent.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          {indent.status === 'pending' && <>
                            <button onClick={() => openEdit(indent)} className="btn btn-ghost btn-sm" title="Edit"><Edit size={14} /></button>
                            <button onClick={() => approveIndent(indent.id)} className="btn btn-ghost btn-sm" title="Approve" data-testid={`approve-btn-${indent.indent_number}`}><Check size={16} color="#059669" /></button>
                            <button onClick={() => rejectIndent(indent.id)} className="btn btn-ghost btn-sm" title="Reject" data-testid={`reject-btn-${indent.indent_number}`}><X size={16} color="#dc2626" /></button>
                          </>}
                          <button onClick={() => viewDetail(indent)} className="btn btn-ghost btn-sm" title="View Details"><Eye size={14} /></button>
                          {indent.status === 'approved' && <>
                            <button onClick={() => openAddQuote(indent)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }} data-testid={`add-quote-${indent.indent_number}`}><Plus size={12} /> Quote</button>
                            <button onClick={() => openCompare(indent)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }} data-testid={`compare-${indent.indent_number}`}><FileText size={12} /> Compare</button>
                          </>}
                          {expandedRow === indent.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </div>
                      </td>
                    </tr>
                    {expandedRow === indent.id && (
                      <tr><td colSpan={7} style={{ background: 'var(--bg-secondary)', padding: '12px' }}>
                        <div style={{ fontSize: '12px' }}>
                          <strong>Items:</strong>
                          <table style={{ fontSize: '11px', marginTop: '6px', width: '100%' }}>
                            <thead><tr><th style={{ padding: '4px 8px' }}>SKU</th><th style={{ padding: '4px 8px' }}>Name</th><th style={{ padding: '4px 8px' }}>Qty</th><th style={{ padding: '4px 8px' }}>Unit</th></tr></thead>
                            <tbody>{(indent.items || []).map((item, i) => <tr key={i}><td style={{ padding: '4px 8px' }}>{item.sku}</td><td style={{ padding: '4px 8px' }}>{item.name}</td><td style={{ padding: '4px 8px' }}>{item.quantity}</td><td style={{ padding: '4px 8px' }}>{item.unit}</td></tr>)}</tbody>
                          </table>
                          {indent.notes && <div style={{ marginTop: '8px' }}><strong>Notes:</strong> {indent.notes}</div>}
                          {indent.sales_order_number && <div style={{ marginTop: '4px' }}><strong>Linked SO:</strong> {indent.sales_order_number}</div>}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Indent Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="indent-modal">
          <div className="modal-content" style={{ maxWidth: '750px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Indent' : 'Create Material Indent'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Department *</label>
                    <select className="form-select" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} required data-testid="dept-select">
                      <option value="">Select...</option>
                      {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <select className="form-select" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                      <option value="normal">Normal</option><option value="high">High</option><option value="low">Low</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '16px 0 8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Items</h3>
                  <button type="button" onClick={addItem} className="btn btn-secondary btn-sm"><Plus size={12} /> Add Item</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px' }}>
                  <thead><tr><th style={{ padding: '6px' }}>SKU *</th><th style={{ padding: '6px' }}>Name</th><th style={{ padding: '6px' }}>Qty *</th><th style={{ padding: '6px' }}>Unit</th><th style={{ padding: '6px' }}>Required By</th><th style={{ padding: '6px', width: '40px' }}></th></tr></thead>
                  <tbody>
                    {formData.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '4px' }}><select className="form-select" value={item.sku} onChange={(e) => updateItem(idx, 'sku', e.target.value)} required style={{ fontSize: '12px', padding: '4px' }} data-testid={`item-sku-${idx}`}><option value="">Select...</option>{inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}</select></td>
                        <td style={{ padding: '4px', fontSize: '11px' }}>{item.name || '-'}</td>
                        <td style={{ padding: '4px' }}><input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 0)} required min={1} style={{ fontSize: '12px', padding: '4px', width: '70px' }} /></td>
                        <td style={{ padding: '4px', fontSize: '11px' }}>{item.unit || '-'}</td>
                        <td style={{ padding: '4px' }}><input type="date" className="form-input" value={item.required_by} onChange={(e) => updateItem(idx, 'required_by', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}>{formData.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="btn btn-ghost btn-sm"><Trash2 size={12} color="red" /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="form-group" style={{ marginTop: '12px' }}><label className="form-label">Notes</label><textarea className="form-input" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-indent-btn">{editing ? 'Update' : 'Create'} Indent</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail View Modal */}
      {showDetailModal && detailData && (
        <div className="modal-overlay" data-testid="detail-modal">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Indent Detail: {detailData.indent.indent_number}</h2></div>
            <div className="modal-body">
              <div className="grid grid-cols-3" style={{ gap: '12px', marginBottom: '16px' }}>
                <div><strong>Department:</strong> {detailData.indent.department}</div>
                <div><strong>Status:</strong> <span className={getStatusBadge(detailData.indent.status)}>{detailData.indent.status}</span></div>
                <div><strong>Created:</strong> {new Date(detailData.indent.created_at).toLocaleDateString()}</div>
                {detailData.indent.approved_by && <div><strong>Approved by:</strong> {detailData.indent.approved_by}</div>}
                {detailData.indent.sales_order_number && <div><strong>Linked SO:</strong> {detailData.indent.sales_order_number}</div>}
              </div>
              <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Items</h3>
              <table style={{ width: '100%', fontSize: '12px', marginBottom: '16px' }}>
                <thead><tr><th>SKU</th><th>Name</th><th>Qty</th><th>Unit</th></tr></thead>
                <tbody>{(detailData.indent.items || []).map((item, i) => <tr key={i}><td>{item.sku}</td><td>{item.name}</td><td>{item.quantity}</td><td>{item.unit}</td></tr>)}</tbody>
              </table>
              <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Quotations ({detailData.quotations.length})</h3>
              {detailData.quotations.length === 0 ? <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>No quotations received yet</p> : (
                <table style={{ width: '100%', fontSize: '12px', marginBottom: '16px' }}>
                  <thead><tr><th>Quote #</th><th>Vendor</th><th>Amount</th><th>Delivery (days)</th><th>Terms</th><th>Selected</th></tr></thead>
                  <tbody>{detailData.quotations.map(q => <tr key={q.id} style={q.selected ? { background: '#e8f5e9' } : {}}>
                    <td className="font-mono">{q.quote_number}</td><td>{q.vendor_name}</td><td className="tabular-nums">{q.total_amount?.toLocaleString()}</td>
                    <td>{q.delivery_days}</td><td>{q.payment_terms}</td><td>{q.selected ? <span className="badge badge-success">Selected</span> : '-'}</td>
                  </tr>)}</tbody>
                </table>
              )}
              <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Purchase Orders ({detailData.purchase_orders.length})</h3>
              {detailData.purchase_orders.length === 0 ? <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>No POs created yet</p> : (
                <table style={{ width: '100%', fontSize: '12px' }}>
                  <thead><tr><th>PO #</th><th>Vendor</th><th>Amount</th><th>Status</th></tr></thead>
                  <tbody>{detailData.purchase_orders.map(po => <tr key={po.id}><td className="font-mono">{po.po_number}</td><td>{po.vendor_name}</td><td className="tabular-nums">{po.total_amount?.toLocaleString()}</td><td><span className={getStatusBadge(po.status)}>{po.status}</span></td></tr>)}</tbody>
                </table>
              )}
            </div>
            <div className="modal-footer"><button onClick={() => setShowDetailModal(false)} className="btn btn-secondary">Close</button></div>
          </div>
        </div>
      )}

      {/* Add Quote Modal */}
      {showQuoteModal && quoteIndent && (
        <div className="modal-overlay" data-testid="quote-modal">
          <div className="modal-content" style={{ maxWidth: '750px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Add Vendor Quote for {quoteIndent.indent_number}</h2></div>
            <form onSubmit={submitQuote}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Vendor *</label>
                    <select className="form-select" value={quoteForm.vendor_id} onChange={(e) => { const v = vendors.find(x => x.id === e.target.value); setQuoteForm(prev => ({ ...prev, vendor_id: e.target.value, vendor_name: v?.name || '' })); }} required data-testid="quote-vendor">
                      <option value="">Select Vendor...</option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Delivery (days)</label><input type="number" className="form-input" value={quoteForm.delivery_days} onChange={(e) => setQuoteForm(prev => ({ ...prev, delivery_days: parseInt(e.target.value) || 0 }))} /></div>
                  <div className="form-group"><label className="form-label">Payment Terms</label><input type="text" className="form-input" value={quoteForm.payment_terms} onChange={(e) => setQuoteForm(prev => ({ ...prev, payment_terms: e.target.value }))} placeholder="e.g. Net 30" /></div>
                  <div className="form-group"><label className="form-label">Remarks</label><input type="text" className="form-input" value={quoteForm.remarks} onChange={(e) => setQuoteForm(prev => ({ ...prev, remarks: e.target.value }))} /></div>
                </div>
                <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '12px 0 8px' }}>Item Pricing</h3>
                <table style={{ width: '100%', fontSize: '12px' }}>
                  <thead><tr><th>SKU</th><th>Name</th><th>Qty</th><th>Unit Price *</th><th>Total</th></tr></thead>
                  <tbody>{quoteForm.items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '4px' }}>{item.sku}</td><td style={{ padding: '4px' }}>{item.name}</td>
                      <td style={{ padding: '4px' }}>{item.quantity}</td>
                      <td style={{ padding: '4px' }}><input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateQuoteItem(idx, 'unit_price', e.target.value)} required style={{ fontSize: '12px', padding: '4px', width: '80px' }} /></td>
                      <td style={{ padding: '4px' }} className="tabular-nums">{(item.total || 0).toLocaleString()}</td>
                    </tr>
                  ))}</tbody>
                </table>
                <div style={{ textAlign: 'right', marginTop: '8px', fontWeight: 700 }}>Total: {quoteForm.total_amount.toLocaleString()}</div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowQuoteModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-quote-btn">Add Quotation</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quote Comparison Modal */}
      {showCompareModal && compareData && (
        <div className="modal-overlay" data-testid="compare-modal">
          <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Quote Comparison - {compareData.indent.indent_number}</h2></div>
            <div className="modal-body">
              {compareData.vendors.length === 0 ? <p style={{ color: 'var(--text-tertiary)' }}>No quotations to compare</p> : (
                <>
                  <table style={{ width: '100%', fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '8px' }}>Item</th>
                        <th style={{ padding: '8px' }}>Req Qty</th>
                        {compareData.vendors.map(v => <th key={v.quote_id} style={{ padding: '8px', background: v.selected ? '#e8f5e9' : '' }}>{v.vendor_name}<br/><span className="text-xs">{v.quote_number}</span></th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {compareData.item_comparison.map((item, i) => (
                        <tr key={i}>
                          <td style={{ padding: '6px' }}><strong>{item.sku}</strong><br/>{item.name}</td>
                          <td style={{ padding: '6px' }} className="tabular-nums">{item.required_qty}</td>
                          {item.vendor_prices.map((vp, j) => {
                            const isLowest = vp.unit_price > 0 && vp.unit_price === Math.min(...item.vendor_prices.filter(x => x.unit_price > 0).map(x => x.unit_price));
                            return <td key={j} style={{ padding: '6px', background: isLowest ? '#e8f5e9' : '' }} className="tabular-nums">{vp.unit_price > 0 ? `${vp.unit_price} / unit = ${vp.total.toLocaleString()}` : '-'}</td>;
                          })}
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid #000' }}>
                        <td colSpan={2} style={{ padding: '8px' }}>Total</td>
                        {compareData.vendors.map(v => <td key={v.quote_id} style={{ padding: '8px' }} className="tabular-nums">{v.total_amount?.toLocaleString()}</td>)}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ padding: '6px' }}>Delivery</td>
                        {compareData.vendors.map(v => <td key={v.quote_id} style={{ padding: '6px' }}>{v.delivery_days} days</td>)}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ padding: '6px' }}>Payment Terms</td>
                        {compareData.vendors.map(v => <td key={v.quote_id} style={{ padding: '6px' }}>{v.payment_terms || '-'}</td>)}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ padding: '6px' }}>Action</td>
                        {compareData.vendors.map(v => <td key={v.quote_id} style={{ padding: '6px' }}>
                          {v.selected ? <span className="badge badge-success">Selected</span> : <button onClick={() => selectQuote(v.quote_id)} className="btn btn-primary btn-sm" style={{ fontSize: '11px' }}>Select Winner</button>}
                        </td>)}
                      </tr>
                    </tbody>
                  </table>
                </>
              )}
            </div>
            <div className="modal-footer"><button onClick={() => setShowCompareModal(false)} className="btn btn-secondary">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialIndents;
